/**
 * Shared types, constants, and utilities for graph visualisation components.
 *
 * Used by both GraphArtefactViewer (2D / D3) and Graph3DViewer (3D / three.js).
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface GraphNode {
  id: string;
  label: string;
  name: string;
  properties: Record<string, string>;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  properties: Record<string, string>;
}

export interface GraphData {
  graph: {
    nodes: GraphNode[];
    edges: GraphEdge[];
  };
  node_count: number;
  edge_count: number;
  title?: string;
}

export interface SelectedNodeInfo {
  name: string;
  label: string;
  properties: Record<string, string>;
  x: number;
  y: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Colour palette for node labels — assigned dynamically */
export const DEFAULT_COLOURS = [
  "#4a7c8f",
  "#c0705b",
  "#5d7ca2",
  "#8a9a5c",
  "#9673a6",
  "#c2956a",
  "#5c8f7c",
  "#a07385",
  "#6f8ea0",
  "#b39070",
];

/* ------------------------------------------------------------------ */
/*  Functions                                                          */
/* ------------------------------------------------------------------ */

/**
 * Build a colour map from unique node labels to palette colours.
 */
export function buildLabelColourMap(nodes: GraphNode[]): Record<string, string> {
  const labels = Array.from(new Set(nodes.map((n) => n.label)));
  const colourMap: Record<string, string> = {};

  labels.forEach((label, i) => {
    colourMap[label] = DEFAULT_COLOURS[i % DEFAULT_COLOURS.length];
  });

  return colourMap;
}

/**
 * Escape HTML special characters to prevent XSS in tooltip markup.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
